import { forwardRef, useCallback, useEffect, useState } from 'react'
import { CarouselBlock, type CommonBlockProps } from '../../../types'
import useEmblaCarousel from 'embla-carousel-react'
import { Block } from '../../Block'
import { webchatClasses } from '../../../styles/classes'
import { ChevronLeft, ChevronRight } from 'lucide-react'

type Props = CarouselBlock & CommonBlockProps
type EmblaCarouselType = ReturnType<typeof useEmblaCarousel>[1]

export const Carousel = forwardRef<HTMLDivElement, Props>(
  ({ blocks, direction, sender, timestamp, messageId, isReadOnly, sendMessage }, ref) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({ skipSnaps: true, loop: true })

    const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
    const [nextBtnDisabled, setNextBtnDisabled] = useState(true)

    const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi])
    const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi])

    const {
      message: {
        blocks: { carousel: carouselStyles },
      },
    } = webchatClasses

    const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
      setPrevBtnDisabled(!emblaApi?.canScrollPrev())
      setNextBtnDisabled(!emblaApi?.canScrollNext())
    }, [])

    useEffect(() => {
      if (!emblaApi) return
      onSelect(emblaApi)
      emblaApi.on('reInit', onSelect)
      emblaApi.on('select', onSelect)
    }, [emblaApi, onSelect])

    return (
      <div {...carouselStyles?.container} data-container="carousel" ref={emblaRef}>
        <div ref={ref} {...carouselStyles?.slidesContainer}>
          {blocks.map((block, index) => (
            <Block key={index} block={{ ...block, direction, timestamp, sender, messageId, sendMessage, isReadOnly }} />
          ))}
        </div>
        {blocks.length > 1 && (
          <>
            <ChevronLeft
              {...carouselStyles?.backButton}
              data-disabled={prevBtnDisabled ? '' : undefined}
              onClick={scrollPrev}
            />

            <ChevronRight
              {...carouselStyles?.nextButton}
              data-disabled={nextBtnDisabled ? '' : undefined}
              onClick={scrollNext}
            />
          </>
        )}
      </div>
    )
  }
)

Carousel.displayName = 'Carousel'
